import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { EXTERNAL_LINKS } from "@/lib/constants";
import { toast } from "sonner";
import { Upload, ExternalLink, ChevronRight, CheckCircle2, UserCircle2, Save, Send, Search as SearchIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const DEPARTMENTS = [
  "IT","Clinical/CHC Operations","Behavioral Health/Crisis Center","School Health",
  "Pharmacy","Finance","HR","Facilities/Supply Chain","Lakeside Medical Center",
  "Aeromedical/Trauma","Executive/Board",
];

const CONTRACT_TYPES = [
  "New","Renewal-Extension","Amendment-Revision",
  "MOU","BAA","NDA","Lease","Grant","Data Sharing","Services",
];

const PROCUREMENT_METHODS = [
  "","Sole Source","RFP","ITB","RFQ","Cooperative / Piggyback","Direct Purchase","Sponsorship","Grant Award",
];

function Section({ title, children, first }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-md ${first ? "" : "mt-6"}`}>
      <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 rounded-t-md">
        <div className="text-xs font-bold uppercase tracking-wider text-[#005289]">{title}</div>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Field({ label, children, required, wide, hint }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <label className="text-xs font-bold text-slate-700 block mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

const input = "w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:border-[#005289] bg-white";
const textarea = "w-full min-h-[100px] p-3 text-sm border border-slate-200 rounded-md focus:border-[#005289] bg-white";

const empty = { answer: "", comments: "" };

function ParentPickerSection({ f, setF, user }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const isLegal = user && ["general_counsel", "legal_manager", "attorney", "contract_analyst", "it_admin"].includes(user.role);

  useEffect(() => {
    const t = setTimeout(() => {
      if (q.length < 1 && !open) return;
      api.get(`/lineage/eligible-parents?q=${encodeURIComponent(q)}`)
        .then(r => setResults(r.data || []))
        .catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  const pick = (row) => {
    setSelected(row);
    setF({ ...f, parent_contract_id: row.id, child_relation: f.contract_type === "Renewal-Extension" ? "renewal" : "amendment" });
    setOpen(false);
  };

  return (
    <div className="bg-amber-50 border border-amber-300 rounded-md mt-6" data-testid="parent-picker-section">
      <div className="px-5 py-3 border-b border-amber-200 bg-amber-100 rounded-t-md">
        <div className="text-xs font-bold uppercase tracking-wider text-amber-900">
          Parent Contract (Required for {f.contract_type})
        </div>
      </div>
      <div className="p-5 space-y-3">
        <div className="text-xs text-amber-900">
          Every amendment / renewal must link to a master contract. This creates the family chain (e.g., 4804 → 4804.1 → 4804.2).
        </div>
        {selected ? (
          <div className="border border-amber-300 bg-white rounded-md p-3 flex items-center justify-between" data-testid="parent-selected">
            <div>
              <div className="font-semibold text-sm">#{selected.contract_number} · {selected.vendor}</div>
              <div className="text-xs text-slate-600">{selected.purpose} · {selected.department} · {selected.stage}</div>
            </div>
            <button
              type="button"
              className="text-xs px-2 py-1 border border-slate-300 rounded hover:bg-slate-50"
              onClick={() => { setSelected(null); setF({ ...f, parent_contract_id: "", child_relation: "" }); }}
              data-testid="parent-clear"
            >Change</button>
          </div>
        ) : (
          <div className="relative">
            <input
              className={input}
              placeholder="Search master contract by #, vendor, or purpose..."
              value={q}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              data-testid="parent-search"
            />
            {open && results.length > 0 && (
              <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-y-auto z-10">
                {results.map(r => (
                  <button
                    type="button"
                    key={r.id}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100"
                    onClick={() => pick(r)}
                    data-testid={`parent-option-${r.contract_number}`}
                  >
                    <div className="text-sm font-semibold">#{r.contract_number} · {r.vendor}</div>
                    <div className="text-xs text-slate-500">{r.purpose} · {r.department}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {isLegal && (
          <div>
            <label className="text-xs font-bold text-slate-700 block mb-1">Legacy contract number (Legal override)</label>
            <input
              className={input}
              placeholder="Optional: enter a legacy number to override auto-generated .N"
              value={f.legacy_contract_number || ""}
              onChange={(e) => setF({ ...f, legacy_contract_number: e.target.value })}
              data-testid="legacy-number-input"
            />
            <div className="text-[11px] text-slate-500 mt-1">Leave blank to auto-generate the next .N number for this parent.</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function IntakeForm() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [submitters, setSubmitters] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [sites, setSites] = useState([]);
  const [legalSuggest, setLegalSuggest] = useState([]);
  const [vendorQuery, setVendorQuery] = useState("");
  const [vendorPickerOpen, setVendorPickerOpen] = useState(false);
  const [f, setF] = useState({
    on_behalf_of_user_id: "",
    submitter_first_name: "", submitter_last_name: "", submitter_email: "",
    bu_approver_id: "", bu_approver_name: "", bu_approver_email: "", business_unit: "",
    bu_reviewer_id: "", bu_reviewer_name: "", bu_reviewer_email: "",
    legal_contact_first_name: "", legal_contact_last_name: "",
    legal_contact_email: "", legal_contact_phone: "",
    purpose: "",
    contract_type: "New", term_length_preferred: "",
    terms_grid: {
      date_needed_by: { ...empty }, term_duration: { ...empty }, can_terminate: { ...empty },
      renewal_terms: { ...empty }, annual_increases: { ...empty }, specific_concerns: { ...empty },
    },
    financial_grid: {
      annual_commitment: { ...empty }, total_commitment: { ...empty }, payment_timing: { ...empty },
      payment_undefined: { ...empty }, finance_committee_approval: { answer: "N/A", comments: "" },
      board_approval: { answer: "N/A", comments: "" },
    },
    vendor_legal_name: "", vendor_dba: "",
    vendor_contact_name: "", vendor_contact_email: "", vendor_contact_phone: "",
    vendor_id: null, site_ids: [],
    is_budgeted: "Yes", contract_amount: 0,
    one_time_cost: 0, capital_cost: 0, po_number: "",
    it_resources_needed: false, handles_phi_pii_pci: false,
    // Purchasing + SRA (Feb 2026 intake merge)
    is_exempt_bids: "", bid_justification: "",
    it_infra_integration: false,
    sra_data_types: [], sra_data_location: "", sra_integration_details: "",
    cpi_percentage: 0, cpi_cap_percentage: 3, annual_support_rate: 0, annual_operating_cost: 0,
    timeline: "21-day", department: "IT",
    hcd_entity: "Health Care District", description: "",
    // Phase 3
    cost_center: "", approving_director_id: "", approving_director_name: "", approving_director_email: "",
    funding_source: "", budget_line: "", procurement_method: "", solicitation_number: "",
    auto_renewal: false, renewal_notice_days: null, termination_notice_days: null,
    expiration_date: "", effective_date: "",
    baa_required: false, insurance_required: false, insurance_requirements: "", board_approval_required: false,
    priority_at_intake: false, priority_reason: "", needed_by_date: "",
    executive_sponsor_name: "", executive_sponsor_email: "", executive_sponsor_title: "",
    // Phase 1 — Restricted (confidential) tag at intake (Legal only)
    is_restricted: false, restricted_reason: "",
    // Phase 3 — Lineage & numbering
    parent_contract_id: "", child_relation: "", legacy_contract_number: "",
    certification_ack: false,
  });
  const [files, setFiles] = useState({});
  const [params] = useSearchParams();

  useEffect(() => {
    api.get("/users/submitters").then(r => setSubmitters(r.data || [])).catch(() => {});
    api.get("/vendors").then(r => setVendors(r.data || [])).catch(() => {});
    api.get("/sites?active_only=true").then(r => setSites(r.data || [])).catch(() => {});
    // Resume draft if ?draft=<id>
    const did = params.get("draft");
    if (did) {
      api.get(`/contracts/${did}`).then(r => {
        setDraftId(did);
        setF(prev => ({ ...prev, ...r.data }));
        toast.info("Resumed draft — save or submit when ready");
      }).catch(() => {});
    }
    // eslint-disable-next-line
  }, []);

  const pickVendor = (v) => {
    setF(prev => ({
      ...prev,
      vendor_id: v.id,
      vendor_legal_name: v.legal_name || prev.vendor_legal_name,
      vendor_dba: v.dba || prev.vendor_dba,
      vendor_contact_name: v.contact_name || prev.vendor_contact_name,
      vendor_contact_email: v.contact_email || prev.vendor_contact_email,
      vendor_contact_phone: v.contact_phone || prev.vendor_contact_phone,
      baa_required: v.baa_required || prev.baa_required,
      insurance_requirements: v.insurance_requirements || prev.insurance_requirements,
    }));
    setVendorPickerOpen(false);
    setVendorQuery(v.legal_name);
  };

  const toggleSite = (siteId) => {
    setF(prev => ({
      ...prev,
      site_ids: prev.site_ids.includes(siteId)
        ? prev.site_ids.filter(x => x !== siteId)
        : [...prev.site_ids, siteId],
    }));
  };

  const filteredVendors = vendors.filter(v =>
    !vendorQuery || (v.legal_name || "").toLowerCase().includes(vendorQuery.toLowerCase()) ||
    (v.dba || "").toLowerCase().includes(vendorQuery.toLowerCase()));

  const pickOnBehalf = (uid) => {
    setF(prev => ({ ...prev, on_behalf_of_user_id: uid }));
    if (!uid) return;
    const u = submitters.find(s => s.id === uid);
    if (!u) return;
    const [first, ...rest] = (u.name || "").split(" ");
    setF(prev => ({
      ...prev,
      on_behalf_of_user_id: uid,
      submitter_first_name: first || prev.submitter_first_name,
      submitter_last_name: rest.join(" ") || prev.submitter_last_name,
      submitter_email: u.email || prev.submitter_email,
      business_unit: u.department || prev.business_unit,
      department: u.department || prev.department,
    }));
  };

  const setGridRow = (grid, key, field, value) => {
    setF(prev => ({ ...prev, [grid]: { ...prev[grid], [key]: { ...prev[grid][key], [field]: value } } }));
  };

  const flagGovernance = Number(f.contract_amount) > 250000 && f.is_budgeted !== "Yes";

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      if (draftId) {
        await api.patch(`/contracts/${draftId}/draft`, f);
        toast.success("Draft saved");
      } else {
        const { data } = await api.post("/contracts/draft", f);
        setDraftId(data.id);
        window.history.replaceState(null, "", `/intake?draft=${data.id}`);
        toast.success("Draft saved — resume anytime from your dashboard");
      }
    } catch (e) {
      console.error("saveDraft failed", e);
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Draft save failed"));
    } finally { setSavingDraft(false); }
  };

  const submit = async () => {
    if (!f.submitter_first_name || !f.submitter_email || !f.vendor_legal_name || !f.purpose || !f.term_length_preferred) {
      toast.error("Please complete the required fields");
      return;
    }
    if (!f.certification_ack) {
      toast.error("Please acknowledge the certification checkbox before submitting");
      return;
    }
    if (!f.is_exempt_bids) {
      toast.error("Please answer the Purchasing exemption question");
      return;
    }
    if (f.is_exempt_bids === "No" && !f.bid_justification?.trim()) {
      toast.error("Please provide bid justification (required when lowest bid is not selected)");
      return;
    }
    if (f.legal_contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.legal_contact_email)) {
      toast.error("Legal Contact email is not a valid address");
      return;
    }
    if (!f.legal_contact_email && !f.legal_contact_phone) {
      toast.error("Provide at least a Legal Contact email OR phone before submitting");
      return;
    }
    if ((f.handles_phi_pii_pci || f.it_infra_integration || f.it_resources_needed)) {
      if (f.sra_data_types.length === 0) { toast.error("SRA: select at least one data type"); return; }
      if (!f.sra_data_location) { toast.error("SRA: select where the data lives"); return; }
      if (!f.sra_integration_details?.trim()) { toast.error("SRA: describe integration details"); return; }
    }
    if ((f.contract_type === "Renewal-Extension" || f.contract_type === "Amendment-Revision") && !files.prior && !f.documents?.some?.(d => d.kind === "prior")) {
      toast.error("Please attach the existing / prior agreement");
      return;
    }
    setSaving(true);
    try {
      let contractId, contractNumber;
      if (draftId) {
        // Save latest edits, then submit-draft (freezes original_submission)
        await api.patch(`/contracts/${draftId}/draft`, f);
        const { data } = await api.post(`/contracts/${draftId}/submit-draft`);
        contractId = data.id; contractNumber = data.contract_number;
      } else {
        const { data } = await api.post("/contracts/intake", f);
        contractId = data.id; contractNumber = data.contract_number;
      }
      // Upload files
      const uploads = Object.entries(files);
      for (const [kind, file] of uploads) {
        if (!file) continue;
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", kind);
        try { await api.post(`/contracts/${contractId}/upload`, fd); } catch (e) { console.error(e); }
      }
      // Kick off AI brief in background if main uploaded
      if (files.main) { api.post(`/contracts/${contractId}/generate-brief`).catch(()=>{}); }
      toast.success(`Contract ${contractNumber} submitted successfully`);
      nav(`/contract/${contractId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="intake-page">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span>HCD Legal Department</span><ChevronRight className="w-3 h-3" /><span className="text-slate-900 font-medium">Contract Request Memo</span>
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mt-1">New Contract Intake</h1>
        <p className="text-sm text-slate-500 mt-1">Complete every field below. Legal will route your request based on Timeline and Department.</p>
      </div>

      {/* Submitting-as banner (logged-in user + login email, auto-captured) */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4 flex items-center gap-3" data-testid="submitting-as-banner">
        <UserCircle2 className="w-5 h-5 text-[#005289]" />
        <div className="flex-1 text-xs">
          <div className="font-bold text-slate-900">
            Submitting as: <span className="text-[#005289]" data-testid="submitting-as-name">{user?.name || "—"}</span>
            {" "}<span className="text-slate-500 font-normal">({user?.role?.replace(/_/g, " ")})</span>
          </div>
          <div className="text-slate-600">
            Login: <span className="font-mono" data-testid="submitting-as-email">{user?.email || "—"}</span>
            {" · "}Department: {user?.department || "—"}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            This identity is stored with the contract record and appears on the audit trail. If you're submitting on behalf of someone else, choose them below.
          </div>
        </div>
      </div>

      {/* Submitter */}
      <Section title="Submitter Information" first>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Sending on behalf of" wide hint="Optional. Select the actual originator — their manager will be notified of the submission.">
            <select className={input} value={f.on_behalf_of_user_id} onChange={e => pickOnBehalf(e.target.value)} data-testid="in-on-behalf">
              <option value="">— Submitting for myself —</option>
              {submitters.map(u => (
                <option key={u.id} value={u.id}>
                  {u.name}{u.department ? ` · ${u.department}` : ""}{u.role ? ` · ${u.role.replace(/_/g," ")}` : ""} — {u.email}
                </option>
              ))}
            </select>
          </Field>
          <Field label="First Name" required><input className={input} value={f.submitter_first_name} onChange={e => setF({...f, submitter_first_name: e.target.value})} data-testid="in-first" /></Field>
          <Field label="Last Name" required><input className={input} value={f.submitter_last_name} onChange={e => setF({...f, submitter_last_name: e.target.value})} data-testid="in-last" /></Field>
          <Field label="Email" required><input className={input} type="email" value={f.submitter_email} onChange={e => setF({...f, submitter_email: e.target.value})} data-testid="in-email" /></Field>
          <Field label="Business Unit" required>
            <select className={input} value={f.business_unit} onChange={e => setF({...f, business_unit: e.target.value, department: e.target.value})} data-testid="in-business-unit">
              <option value="">Select...</option>
              {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>
          <Field label="Business Unit Approver" required>
            <select className={input} value={f.bu_approver_id}
                    onChange={e => {
                      const uid = e.target.value;
                      const u = submitters.find(s => s.id === uid);
                      setF(prev => ({
                        ...prev,
                        bu_approver_id: uid,
                        bu_approver_name: u ? u.name : "",
                        bu_approver_email: u ? (u.email || "") : "",
                      }));
                    }} data-testid="in-bu-approver">
              <option value="">— Select approver —</option>
              {submitters
                .filter(u => !f.business_unit || u.department === f.business_unit || u.role === "department_director")
                .map(u => <option key={u.id} value={u.id}>{u.name} · {u.title || u.role.replace(/_/g, " ")}{u.department ? ` · ${u.department}` : ""}</option>)}
            </select>
          </Field>
          <Field label="Approver Email" required>
            <input className={input} type="email" value={f.bu_approver_email} readOnly data-testid="in-bu-approver-email" />
          </Field>
          <Field label="Who from the Business Unit reviewed">
            <select className={input} value={f.bu_reviewer_id}
                    onChange={e => {
                      const uid = e.target.value;
                      const u = submitters.find(s => s.id === uid);
                      setF(prev => ({
                        ...prev,
                        bu_reviewer_id: uid,
                        bu_reviewer_name: u ? u.name : "",
                        bu_reviewer_email: u ? (u.email || "") : "",
                      }));
                    }} data-testid="in-bu-reviewer">
              <option value="">— Select reviewer —</option>
              {submitters
                .filter(u => !f.business_unit || u.department === f.business_unit || u.role === "department_director")
                .map(u => <option key={u.id} value={u.id}>{u.name} · {u.title || u.role.replace(/_/g, " ")}{u.department ? ` · ${u.department}` : ""}</option>)}
            </select>
          </Field>
          <Field label="Legal Contact — First"><input className={input} value={f.legal_contact_first_name} onChange={e => setF({...f, legal_contact_first_name: e.target.value})} /></Field>
          <Field label="Legal Contact — Last"><input className={input} value={f.legal_contact_last_name} onChange={e => setF({...f, legal_contact_last_name: e.target.value})} /></Field>
          <Field label="Legal Contact — Email">
            <input className={input} type="email" value={f.legal_contact_email}
                   onChange={e => {
                     const v = e.target.value;
                     setF({...f, legal_contact_email: v});
                     if (v.length >= 2) {
                       api.get(`/legal-contacts/suggest?q=${encodeURIComponent(v)}`)
                         .then(r => setLegalSuggest(r.data || []))
                         .catch(() => setLegalSuggest([]));
                     } else setLegalSuggest([]);
                   }}
                   onBlur={() => setTimeout(() => setLegalSuggest([]), 200)}
                   data-testid="in-legal-email" placeholder="legal@vendor.com" />
            {legalSuggest.length > 0 && (
              <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-56 overflow-y-auto"
                   data-testid="legal-email-suggest">
                {legalSuggest.map(s => (
                  <button key={s.email} type="button" onClick={() => {
                    setF({...f,
                      legal_contact_email: s.email,
                      legal_contact_first_name: s.first || f.legal_contact_first_name,
                      legal_contact_last_name:  s.last  || f.legal_contact_last_name,
                      legal_contact_phone:      s.phone || f.legal_contact_phone,
                    });
                    setLegalSuggest([]);
                  }} data-testid={`legal-suggest-${s.email}`}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 text-xs border-b border-slate-100 last:border-0">
                    <div className="font-semibold text-slate-800">{s.email}</div>
                    <div className="text-[10px] text-slate-500">{s.first} {s.last} {s.phone ? `· ${s.phone}` : ""} · used on {s.count} contract{s.count === 1 ? "" : "s"}{s.vendor ? ` (${s.vendor})` : ""}</div>
                  </button>
                ))}
              </div>
            )}
          </Field>
          <Field label="Legal Contact — Phone"><input className={input} type="tel" value={f.legal_contact_phone} onChange={e => setF({...f, legal_contact_phone: e.target.value})} data-testid="in-legal-phone" placeholder="561-555-0100" /></Field>
        </div>
        <div className="mt-4 border-t border-slate-100 pt-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Executive Sponsor <span className="text-slate-400 font-normal normal-case">— surfaced on the GC's Enterprise dashboard "Contracts by Executive Sponsor"</span></div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Executive Sponsor — Name"><input className={input} value={f.executive_sponsor_name} onChange={e => setF({...f, executive_sponsor_name: e.target.value})} placeholder="e.g., Susan Chen" data-testid="in-exec-sponsor-name" /></Field>
            <Field label="Executive Sponsor — Title"><input className={input} value={f.executive_sponsor_title} onChange={e => setF({...f, executive_sponsor_title: e.target.value})} placeholder="e.g., Chief Medical Officer" data-testid="in-exec-sponsor-title" /></Field>
            <Field label="Executive Sponsor — Email"><input className={input} type="email" value={f.executive_sponsor_email} onChange={e => setF({...f, executive_sponsor_email: e.target.value})} data-testid="in-exec-sponsor-email" /></Field>
          </div>
        </div>
      </Section>

      {/* Purpose */}
      <Section title="Purpose of Contract">
        <Field label="Purpose / Objective / Financials / Concerns / History" required hint="Provide as much detail as possible; this drives the Director's Brief.">
          <textarea className={textarea} value={f.purpose} onChange={e => setF({...f, purpose: e.target.value})} data-testid="in-purpose" />
        </Field>
      </Section>

      {/* Type */}
      <Section title="Contract Type">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Type" required>
            <select className={input} value={f.contract_type} onChange={e => setF({...f, contract_type: e.target.value})} data-testid="in-type">
              {CONTRACT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Term-Length Preferred" required><input className={input} value={f.term_length_preferred} onChange={e => setF({...f, term_length_preferred: e.target.value})} placeholder="e.g., 3 years with 1 renewal" /></Field>
          <Field label="Timeline (SLA)">
            <select className={input} value={f.timeline} onChange={e => setF({...f, timeline: e.target.value})} data-testid="in-timeline">
              <option value="21-day">21-day (Standard)</option>
              <option value="40-day">40-day (Complex)</option>
              <option value="Expedited">Expedited (5 business days)</option>
            </select>
          </Field>
          <Field label="Department"><select className={input} value={f.department} onChange={e => setF({...f, department: e.target.value})}>
            {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
          </select></Field>
        </div>
      </Section>

      {/* Phase 3 — Parent contract picker (REQUIRED for Amendment/Renewal-Extension) */}
      {(f.contract_type === "Amendment-Revision" || f.contract_type === "Renewal-Extension") && (
        <ParentPickerSection f={f} setF={setF} user={user} />
      )}

      {/* Contract Terms Grid — conditional on Renewal/Extension/Amendment */}
      {(f.contract_type === "Renewal-Extension" || f.contract_type === "Amendment-Revision") && (
      <Section title={`Contract Terms — ${f.contract_type === "Renewal-Extension" ? "Renewal / Extension" : "Amendment / Revision"}`}>
        <div className="text-xs text-slate-500 mb-3">These questions appear because the contract is being renewed or amended — please describe the existing terms and any changes.</div>
        <div className="border border-slate-200 rounded-md overflow-hidden">
          <table className="hcd-table" data-testid="intake-terms-grid">
            <thead><tr><th>Item</th><th>Answer</th><th>Comments</th></tr></thead>
            <tbody>
              {[
                ["date_needed_by", "Date Needed By"],
                ["term_duration", "Term (Duration as listed in current contract)"],
                ["can_terminate", "Can we terminate without cause / penalty?"],
                ...(f.contract_type === "Renewal-Extension" ? [["renewal_terms", "Renewal Terms (if any)"]] : []),
                ["annual_increases", "Annual price increases / financial changes"],
                ["specific_concerns", "Specific Terms/Conditions of concern"],
                ["special_terms_to_address", "Special terms to bring to Legal's attention"],
                ["additional_terms_wanted", "Additional terms you'd like to include"],
              ].map(([k, label]) => (
                <tr key={k}>
                  <td className="font-semibold w-64">{label}</td>
                  <td><input className={input} data-testid={`grid-terms-${k}`} value={f.terms_grid[k]?.answer || ""} onChange={e => setGridRow("terms_grid", k, "answer", e.target.value)} /></td>
                  <td><input className={input} value={f.terms_grid[k]?.comments || ""} onChange={e => setGridRow("terms_grid", k, "comments", e.target.value)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
      )}

      {/* Contract Terms Grid — condensed for NEW contracts */}
      {f.contract_type !== "Renewal-Extension" && f.contract_type !== "Amendment-Revision" && (
      <Section title="Contract Terms">
        <div className="border border-slate-200 rounded-md overflow-hidden">
          <table className="hcd-table">
            <thead><tr><th>Item</th><th>Answer</th><th>Comments</th></tr></thead>
            <tbody>
              {[
                ["date_needed_by", "Date Needed By"],
                ["term_duration", "Term (as listed in contract)"],
                ["can_terminate", "Can we terminate the agreement?"],
                ["annual_increases", "Any increases annually or on renewal?"],
                ["specific_concerns", "Any specific Terms/Conditions of concern?"],
              ].map(([k, label]) => (
                <tr key={k}>
                  <td className="font-semibold w-64">{label}</td>
                  <td><input className={input} value={f.terms_grid[k]?.answer || ""} onChange={e => setGridRow("terms_grid", k, "answer", e.target.value)} /></td>
                  <td><input className={input} value={f.terms_grid[k]?.comments || ""} onChange={e => setGridRow("terms_grid", k, "comments", e.target.value)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
      )}

      {/* Financial */}
      <Section title="Financial Commitment">
        <div className="border border-slate-200 rounded-md overflow-hidden mb-4">
          <table className="hcd-table">
            <thead><tr><th>Item</th><th>Answer</th><th>Comments</th></tr></thead>
            <tbody>
              {[
                ["annual_commitment", "Annual Financial Commitment"],
                ["total_commitment", "Total Financial Commitment over Term"],
                ["payment_timing", "When is payment due (advance/annually/arrears/monthly)?"],
                ["payment_undefined", "If terms undefined, when/how to pay vendor?"],
              ].map(([k, label]) => (
                <tr key={k}>
                  <td className="font-semibold w-64">{label}</td>
                  <td><input className={input} value={f.financial_grid[k].answer} onChange={e => setGridRow("financial_grid", k, "answer", e.target.value)} /></td>
                  <td><input className={input} value={f.financial_grid[k].comments} onChange={e => setGridRow("financial_grid", k, "comments", e.target.value)} /></td>
                </tr>
              ))}
              {[["finance_committee_approval","Finance Committee Approval"],["board_approval","Board Approval"]].map(([k,label]) => (
                <tr key={k}>
                  <td className="font-semibold w-64">{label}</td>
                  <td><select className={input} value={f.financial_grid[k].answer} onChange={e => setGridRow("financial_grid", k, "answer", e.target.value)}>
                    <option>Yes</option><option>No</option><option>N/A</option>
                  </select></td>
                  <td><input className={input} value={f.financial_grid[k].comments} onChange={e => setGridRow("financial_grid", k, "comments", e.target.value)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Contract Amount (USD)"><input className={input} type="number" value={f.contract_amount} onChange={e => setF({...f, contract_amount: e.target.value})} data-testid="in-amount" /></Field>
          <Field label="Is amount budgeted?"><select className={input} value={f.is_budgeted} onChange={e => setF({...f, is_budgeted: e.target.value})}>
            <option>Yes</option><option>No</option><option>N/A</option>
          </select></Field>
        </div>
        <div className="grid grid-cols-3 gap-4 mt-3">
          <Field label="One-Time Cost (USD)" hint="Setup / implementation / kick-off fee (not recurring)">
            <input className={input} type="number" value={f.one_time_cost}
                   onChange={e => setF({...f, one_time_cost: parseFloat(e.target.value) || 0})}
                   data-testid="in-onetime" />
          </Field>
          <Field label="Capital Cost (USD)" hint="Depreciable equipment / infrastructure spend">
            <input className={input} type="number" value={f.capital_cost}
                   onChange={e => setF({...f, capital_cost: parseFloat(e.target.value) || 0})}
                   data-testid="in-capital" />
          </Field>
          <Field label="Purchase Order (PO) #" hint="Optional — linked PO number">
            <input className={input} value={f.po_number}
                   onChange={e => setF({...f, po_number: e.target.value})}
                   data-testid="in-po" />
          </Field>
        </div>
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-slate-700">
          <b>Governance rule:</b> Finance Committee & Board approval required if amount over $250,000 and unbudgeted, all construction agreements, and all real-property leases.
        </div>
        <div className="mt-4 pt-4 border-t border-slate-200">
          <div className="text-xs font-bold text-slate-700 mb-3">CPI, Support & Operating Cost</div>
          <div className="grid grid-cols-4 gap-4">
            <Field label="CPI Escalator %" hint="Annual price increase tied to CPI-U">
              <input className={input} type="number" step="0.1" value={f.cpi_percentage}
                     onChange={e => setF({...f, cpi_percentage: parseFloat(e.target.value) || 0})}
                     data-testid="in-cpi" />
            </Field>
            <Field label="CPI Cap %" hint="Max increase per year (typical: 3%)">
              <input className={input} type="number" step="0.1" value={f.cpi_cap_percentage}
                     onChange={e => setF({...f, cpi_cap_percentage: parseFloat(e.target.value) || 0})}
                     data-testid="in-cpi-cap" />
            </Field>
            <Field label="Annual Support/Maint. Rate $" hint="Recurring support fee">
              <input className={input} type="number" value={f.annual_support_rate}
                     onChange={e => setF({...f, annual_support_rate: parseFloat(e.target.value) || 0})}
                     data-testid="in-support" />
            </Field>
            <Field label="Annual Operating Cost $" hint="Ongoing operating expense">
              <input className={input} type="number" value={f.annual_operating_cost}
                     onChange={e => setF({...f, annual_operating_cost: parseFloat(e.target.value) || 0})}
                     data-testid="in-operating" />
            </Field>
          </div>
        </div>
        {flagGovernance && (
          <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700 flex items-center gap-2">
            <span className="chip chip-overdue"><span className="dot" />Governance Flag</span>
            This contract exceeds $250,000 and is unbudgeted. Board / Finance Committee approval will be required.
          </div>
        )}
      </Section>

      {/* Vendor */}
      <Section title="Vendor Information">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Vendor (from master)" wide hint="Pick from the Vendor Master. FEIN / contact info will auto-fill. If not listed, type below.">
            <div className="relative">
              <div className="relative">
                <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input data-testid="in-vendor-picker" className={`${input} pl-9`} value={vendorQuery}
                       placeholder="Search vendor master..."
                       onChange={e => { setVendorQuery(e.target.value); setVendorPickerOpen(true); setF(prev => ({...prev, vendor_id: null, vendor_legal_name: e.target.value})); }}
                       onFocus={() => setVendorPickerOpen(true)}
                       onBlur={() => setTimeout(() => setVendorPickerOpen(false), 200)} />
              </div>
              {vendorPickerOpen && filteredVendors.length > 0 && (
                <div className="absolute z-20 top-full left-0 right-0 max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg mt-1" data-testid="vendor-picker-dropdown">
                  {filteredVendors.slice(0, 20).map(v => (
                    <button key={v.id} type="button" onMouseDown={() => pickVendor(v)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100"
                            data-testid={`vendor-pick-${v.id}`}>
                      <div className="font-medium text-slate-900">{v.legal_name}</div>
                      <div className="text-xs text-slate-500 flex gap-3">
                        {v.fein && <span>FEIN {v.fein}</span>}
                        {v.baa_required && <span className={v.baa_on_file ? "text-emerald-700" : "text-red-600"}>BAA {v.baa_on_file ? "✓" : "⚠"}</span>}
                        {v.contact_email && <span>{v.contact_email}</span>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </Field>
          <Field label="Legal Name of Vendor" required><input className={input} value={f.vendor_legal_name} onChange={e => setF({...f, vendor_legal_name: e.target.value, vendor_id: null})} data-testid="in-vendor" /></Field>
          <Field label="DBA (Doing Business As)"><input className={input} value={f.vendor_dba} onChange={e => setF({...f, vendor_dba: e.target.value})} /></Field>
          <Field label="Vendor Contact Name" required><input className={input} value={f.vendor_contact_name} onChange={e => setF({...f, vendor_contact_name: e.target.value})} /></Field>
          <Field label="Vendor Contact Email" required><input className={input} type="email" value={f.vendor_contact_email} onChange={e => setF({...f, vendor_contact_email: e.target.value})} /></Field>
          <Field label="Vendor Contact Phone"><input className={input} value={f.vendor_contact_phone} onChange={e => setF({...f, vendor_contact_phone: e.target.value})} /></Field>
        </div>
      </Section>

      {/* Sites, Cost Center, Funding — Phase 3 */}
      <Section title="Cost Center · Sites · Funding">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Cost Center">
            <input data-testid="in-cost-center" className={input} value={f.cost_center} onChange={e => setF({...f, cost_center: e.target.value})} placeholder="e.g., 100-2345"/>
          </Field>
          <Field label="Approving Director / VP">
            <select data-testid="in-approving-director" className={input} value={f.approving_director_id || ""}
                    onChange={e => {
                      const u = submitters.find(s => s.id === e.target.value);
                      setF(prev => ({...prev,
                        approving_director_id: e.target.value,
                        approving_director_name: u?.name || "",
                        approving_director_email: u?.email || ""}));
                    }}>
              <option value="">— Select director / VP —</option>
              {submitters.filter(u => u.role === "department_director" || u.role === "general_counsel").map(u =>
                <option key={u.id} value={u.id}>{u.name} — {u.title || u.role} · {u.department || "—"}</option>
              )}
            </select>
          </Field>
          <Field label="Funding Source">
            <select className={input} value={f.funding_source} onChange={e => setF({...f, funding_source: e.target.value})}>
              <option value="">Select...</option>
              <option>General Fund</option><option>Grant</option><option>Capital</option>
              <option>Restricted Fund</option><option>Sales Tax</option><option>Other</option>
            </select>
          </Field>
          <Field label="Budget Line / GL Account"><input className={input} value={f.budget_line} onChange={e => setF({...f, budget_line: e.target.value})}/></Field>
          <Field label="Procurement Method">
            <select data-testid="in-proc-method" className={input} value={f.procurement_method} onChange={e => setF({...f, procurement_method: e.target.value})}>
              {PROCUREMENT_METHODS.map(p => <option key={p} value={p}>{p || "Select..."}</option>)}
            </select>
          </Field>
          <Field label="Solicitation #"><input className={input} value={f.solicitation_number} onChange={e => setF({...f, solicitation_number: e.target.value})} placeholder="RFP-24-0001"/></Field>
        </div>
        <div className="mt-4">
          <label className="text-xs font-bold text-slate-700 block mb-2">Sites <span className="text-slate-400">(multi-select from Site Master)</span></label>
          <div className="flex flex-wrap gap-2" data-testid="in-sites">
            {sites.map(s => {
              const on = f.site_ids.includes(s.id);
              return (
                <button key={s.id} type="button" onClick={() => toggleSite(s.id)}
                        data-testid={`site-chip-${s.id}`}
                        className={`text-xs px-3 py-1 rounded-full border ${on ? "bg-[#005289] text-white border-[#005289]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>
                  {on && "✓ "}{s.code ? `${s.code} — ` : ""}{s.name}
                </button>
              );
            })}
            {sites.length === 0 && <div className="text-xs text-slate-400">No sites available.</div>}
          </div>
        </div>
      </Section>

      {/* Renewal / Notice / Dates — Phase 3 */}
      <Section title="Term Dates · Renewal · Notice">
        <div className="grid grid-cols-3 gap-4">
          <Field label="Effective Date"><input data-testid="in-effective" type="date" className={input} value={f.effective_date || ""} onChange={e => setF({...f, effective_date: e.target.value})}/></Field>
          <Field label="Expiration Date"><input data-testid="in-expiration" type="date" className={input} value={f.expiration_date || ""} onChange={e => setF({...f, expiration_date: e.target.value})}/></Field>
          <Field label="Auto-Renewal?">
            <label className="flex items-center gap-2 text-sm pt-2"><input data-testid="in-auto-renewal" type="checkbox" checked={f.auto_renewal} onChange={e => setF({...f, auto_renewal: e.target.checked})}/> Yes, auto-renews</label>
          </Field>
          <Field label="Renewal Notice (days)" hint="Notice required to opt-out before auto-renewal">
            <input className={input} type="number" min="0" value={f.renewal_notice_days ?? ""} onChange={e => setF({...f, renewal_notice_days: e.target.value ? parseInt(e.target.value) : null})}/>
          </Field>
          <Field label="Termination Notice (days)">
            <input className={input} type="number" min="0" value={f.termination_notice_days ?? ""} onChange={e => setF({...f, termination_notice_days: e.target.value ? parseInt(e.target.value) : null})}/>
          </Field>
        </div>
      </Section>

      {/* IT / Security / Compliance requirements */}
      <Section title="Compliance & Security Requirements">
        <div className="grid grid-cols-2 gap-4">
          <Field label="IT resources needed / integration with HCD infrastructure?">
            <label className="flex items-center gap-2 text-sm pt-2"><input data-testid="in-it-resources" type="checkbox" checked={f.it_resources_needed} onChange={e => setF({...f, it_resources_needed: e.target.checked})} /> Yes, IT resources are involved</label>
          </Field>
          <Field label="Handles PHI / PII / PCI?">
            <label className="flex items-center gap-2 text-sm pt-2"><input data-testid="in-phi-pii" type="checkbox" checked={f.handles_phi_pii_pci} onChange={e => setF({...f, handles_phi_pii_pci: e.target.checked})} /> Yes — BAA / Data protection required</label>
          </Field>
          <Field label="Will supplier integrate with HCD IT infrastructure?">
            <label className="flex items-center gap-2 text-sm pt-2"><input data-testid="in-it-infra" type="checkbox" checked={f.it_infra_integration} onChange={e => setF({...f, it_infra_integration: e.target.checked})} /> Yes — direct system access / integration</label>
          </Field>
          <Field label="BAA required?">
            <label className="flex items-center gap-2 text-sm pt-2"><input data-testid="in-baa-req" type="checkbox" checked={f.baa_required} onChange={e => setF({...f, baa_required: e.target.checked})}/> Business Associate Agreement required</label>
          </Field>
          <Field label="Insurance required?">
            <label className="flex items-center gap-2 text-sm pt-2"><input type="checkbox" checked={f.insurance_required} onChange={e => setF({...f, insurance_required: e.target.checked})}/> Certificate of Insurance required</label>
          </Field>
          <Field label="Board approval required?">
            <label className="flex items-center gap-2 text-sm pt-2"><input type="checkbox" checked={f.board_approval_required} onChange={e => setF({...f, board_approval_required: e.target.checked})}/> Yes — must go to Board</label>
          </Field>
          <Field label="Insurance Requirements" wide><textarea rows={2} className={textarea} value={f.insurance_requirements} onChange={e => setF({...f, insurance_requirements: e.target.value})} placeholder="e.g., $1M general liability, $3M professional, HCD as additional insured"/></Field>
        </div>

        {/* Phase 1 — Restricted flag at intake (Legal only) */}
        {["general_counsel", "legal_manager", "attorney", "contract_analyst", "it_admin"].includes(user?.role) && (
          <div className="mt-5 pt-5 border-t border-slate-200 bg-red-50/40 -mx-5 px-5 py-4 rounded-b-md" data-testid="intake-restricted-section">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-bold uppercase tracking-wider text-red-700">🔒 Restricted (Confidential) — Legal only</span>
              <span className="text-[10px] text-red-600 bg-red-100 border border-red-200 rounded-full px-2 py-0.5">Excludes from search, AI, dashboards, non-Legal repository</span>
            </div>
            <label className="flex items-center gap-2 text-sm mb-2">
              <input type="checkbox" data-testid="in-restricted"
                     checked={f.is_restricted}
                     onChange={e => setF({...f, is_restricted: e.target.checked})} />
              Mark as <b>Restricted</b> at submission (e.g., physician/dentist employment, executive employment, managed care)
            </label>
            {f.is_restricted && (
              <input type="text" data-testid="in-restricted-reason"
                     value={f.restricted_reason}
                     onChange={e => setF({...f, restricted_reason: e.target.value})}
                     placeholder="Reason for restriction (audit-logged)"
                     className={input} />
            )}
          </div>
        )}

        {/* SRA sub-section — conditional */}
        {(f.handles_phi_pii_pci || f.it_infra_integration || f.it_resources_needed) && (
          <div className="mt-5 pt-5 border-t border-slate-200" data-testid="sra-subsection">
            <div className="flex items-center gap-2 mb-3">
              <div className="text-xs font-bold uppercase tracking-wider text-[#005289]">Security Risk Assessment (SRA)</div>
              <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">Triggered by your selections</span>
            </div>
            <div className="text-[11px] text-slate-500 mb-3">
              An SRA is triggered when the supplier handles sensitive data or integrates with HCD IT.
              Detail the data footprint below so IT Security can complete the review before execution.
            </div>
            <Field label="Type(s) of sensitive/confidential data involved" required>
              <div className="flex flex-wrap gap-2" data-testid="sra-data-types">
                {["PHI","PII","PCI","Financial","Intellectual Property","Employee Records","Other"].map(dt => {
                  const on = f.sra_data_types.includes(dt);
                  return (
                    <button key={dt} type="button" data-testid={`sra-type-${dt}`}
                            onClick={() => setF(prev => ({...prev, sra_data_types: on ? prev.sra_data_types.filter(x => x !== dt) : [...prev.sra_data_types, dt]}))}
                            className={`text-xs px-3 py-1 rounded-full border ${on ? "bg-[#005289] text-white border-[#005289]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>
                      {on && "✓ "}{dt}
                    </button>
                  );
                })}
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <Field label="Where does the data live?" required>
                <select data-testid="in-sra-location" className={input} value={f.sra_data_location} onChange={e => setF({...f, sra_data_location: e.target.value})}>
                  <option value="">Select...</option>
                  <option>On-Premises (HCD data center)</option>
                  <option>Vendor SaaS / Cloud</option>
                  <option>Hybrid (On-Prem + Cloud)</option>
                  <option>Third-Party Sub-Processor</option>
                  <option>Not Applicable</option>
                </select>
              </Field>
              <Field label="Integration Details (systems, protocols, data flow)" required>
                <textarea data-testid="in-sra-integration" rows={3} className={textarea} value={f.sra_integration_details}
                          onChange={e => setF({...f, sra_integration_details: e.target.value})}
                          placeholder="e.g., Vendor reads/writes to Epic via HL7 FHIR API; nightly SFTP for eligibility file"/>
              </Field>
            </div>
          </div>
        )}
      </Section>

      {/* Purchasing Requirements — from Contract Request Memo PDFs */}
      <Section title="Purchasing Requirements & Supplier Selection">
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-slate-700 mb-3">
          Any expenditure exceeding $10,000 requires the submission of three (3) competitive bids in total,
          including the selected supplier.
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Is this request exempt per the Purchasing Policy from competing bids or quotes?" required>
            <select data-testid="in-exempt-bids" className={input} value={f.is_exempt_bids || ""} onChange={e => setF({...f, is_exempt_bids: e.target.value})}>
              <option value="">Select...</option>
              <option>Yes</option>
              <option>No</option>
              <option>N/A</option>
            </select>
          </Field>
          {f.is_exempt_bids === "No" && (
            <Field label="Justification if the lowest bid is not selected" wide>
              <textarea data-testid="in-bid-justification" rows={3} className={textarea} value={f.bid_justification}
                        onChange={e => setF({...f, bid_justification: e.target.value})}
                        placeholder="Required when the lowest bid is not selected — describe rationale (quality, prior relationship, timeline, etc.)"/>
            </Field>
          )}
        </div>
      </Section>

      {/* Priority at intake — Phase 3 */}
      <Section title="Priority (optional)">
        <div className="grid grid-cols-3 gap-4">
          <Field label="Flag as priority at intake?">
            <label className="flex items-center gap-2 text-sm pt-2"><input data-testid="in-priority" type="checkbox" checked={f.priority_at_intake} onChange={e => setF({...f, priority_at_intake: e.target.checked})}/> Yes, prioritize</label>
          </Field>
          <Field label="Needed by">
            <input type="date" className={input} value={f.needed_by_date || ""} onChange={e => setF({...f, needed_by_date: e.target.value})}/>
          </Field>
          <Field label="Reason" wide={false} hint="Short business justification">
            <input className={input} value={f.priority_reason} onChange={e => setF({...f, priority_reason: e.target.value})} placeholder="e.g., grant deadline, patient safety"/>
          </Field>
        </div>
      </Section>

      {/* Documents */}
      <Section title="Document Uploads">
        <div className="grid grid-cols-2 gap-4">
          {[
            ["main", "Vendor's Contract to be Reviewed", true, null],
            ["prior", "Existing / Prior Agreement", (f.contract_type === "Renewal-Extension" || f.contract_type === "Amendment-Revision"), null],
            ["w9", "W9 (signed within 1 year)", false, null],
            ["coi", "Certificate of Insurance", true, null],
            ["sunbiz", "SunBiz Registration screenshot", true, EXTERNAL_LINKS.sunbiz],
            ["oig", "OIG Exclusions Search", true, EXTERNAL_LINKS.oig],
            ["sam", "SAM.gov Search", true, EXTERNAL_LINKS.sam],
            ["it_security", "IT Security Questionnaire", (f.handles_phi_pii_pci || f.it_infra_integration), null],
          ].map(([kind, label, required, link]) => (
            <div key={kind} className="border border-dashed border-slate-300 rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-slate-800">{label}{required && <span className="text-red-500"> *</span>}</div>
                {link && <a href={link} target="_blank" rel="noreferrer" className="text-[10px] text-[#005289] hover:underline flex items-center gap-1"><ExternalLink className="w-3 h-3" />Search</a>}
              </div>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="file" className="hidden" onChange={e => setFiles(prev => ({...prev, [kind]: e.target.files[0]}))} data-testid={`file-${kind}`} />
                <span className="inline-flex items-center gap-1 px-2 py-1.5 border border-slate-200 rounded text-xs bg-slate-50">
                  <Upload className="w-3 h-3" /> Choose file
                </span>
                {files[kind] && <span className="text-[11px] text-green-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />{files[kind].name}</span>}
              </label>
            </div>
          ))}
        </div>
      </Section>

      {/* Certification + Submit */}
      <div className="mt-6 bg-amber-50 border border-amber-200 rounded-md p-4" data-testid="certification-block">
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" data-testid="in-certification" checked={f.certification_ack}
                 onChange={e => setF({...f, certification_ack: e.target.checked})}
                 className="mt-1"/>
          <span>
            <b>Certification.</b> I certify that the information above is accurate to the best of my knowledge,
            that I am authorized to submit this contract request on behalf of my department, and that I understand
            the request will be reviewed by HCD Legal and routed to the appropriate approver(s).
          </span>
        </label>
      </div>

      {/* Submit */}
      <div className="mt-4 flex items-center justify-end gap-3">
        <button onClick={() => nav(-1)} className="h-10 px-5 border border-slate-200 rounded-md text-sm font-semibold hover:bg-slate-50">Cancel</button>
        <button data-testid="intake-save-draft" onClick={saveDraft} disabled={savingDraft}
                className="h-10 px-5 border border-slate-300 rounded-md text-sm font-semibold hover:bg-slate-50 flex items-center gap-2 disabled:opacity-60">
          <Save className="w-4 h-4"/> {savingDraft ? "Saving..." : (draftId ? "Update Draft" : "Save Draft")}
        </button>
        <button data-testid="intake-submit" onClick={submit} disabled={saving}
                className="h-10 px-6 rounded-md text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-2"
                style={{ background: "#005289" }}>
          <Send className="w-4 h-4"/> {saving ? "Submitting..." : "Submit Intake"}
        </button>
      </div>
    </div>
  );
}
